import { logger } from '@/utils/logger';

import { toast } from '@/hooks/use-toast';

// Common error types
export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  UNKNOWN = 'UNKNOWN'
}

// Custom error class
export class AppError extends Error {
  public readonly type: ErrorType;
  public readonly userMessage: string;
  public readonly originalError?: unknown;

  constructor(
    type: ErrorType,
    userMessage: string,
    originalError?: unknown,
    message?: string
  ) {
    super(message || userMessage);
    this.type = type;
    this.userMessage = userMessage;
    this.originalError = originalError;
    this.name = 'AppError';
  }
}

// Error mapping for common Supabase errors
const ERROR_MAPPINGS = {
  // Network errors
  'Failed to fetch': {
    type: ErrorType.NETWORK,
    message: 'Unable to connect to the server. Please check your internet connection.'
  },
  'NetworkError': {
    type: ErrorType.NETWORK,
    message: 'Network error occurred. Please try again.'
  },
  
  // Authentication errors
  'Invalid login credentials': {
    type: ErrorType.AUTHENTICATION,
    message: 'Invalid email or password. Please try again.'
  },
  'Email not confirmed': {
    type: ErrorType.AUTHENTICATION,
    message: 'Please check your email and confirm your account before signing in.'
  },
  'User not found': {
    type: ErrorType.AUTHENTICATION,
    message: 'No account found with this email address.'
  },
  
  // Authorization errors
  'insufficient_privilege': {
    type: ErrorType.AUTHORIZATION,
    message: 'You do not have permission to perform this action.'
  },
  'new row violates row-level security policy': {
    type: ErrorType.AUTHORIZATION,
    message: 'You do not have permission to access this resource.'
  },
  
  // Validation errors
  'duplicate key value violates unique constraint': {
    type: ErrorType.VALIDATION,
    message: 'This item already exists. Please use a different value.'
  },
  'violates foreign key constraint': {
    type: ErrorType.VALIDATION,
    message: 'Unable to complete action due to data constraints.'
  },
  'check constraint': {
    type: ErrorType.VALIDATION,
    message: 'The provided data does not meet the required criteria.'
  },
  
  // Not found errors
  'The result contains 0 rows': {
    type: ErrorType.NOT_FOUND,
    message: 'The requested item could not be found.'
  },
  
  // Storage errors
  'The resource already exists': {
    type: ErrorType.VALIDATION,
    message: 'A file with this name already exists.'
  },
  'Bucket already exists': {
    type: ErrorType.VALIDATION,
    message: 'This resource already exists.'
  }
};

// Parse error and return user-friendly message
export function parseError(error: unknown): AppError {
  logger.error('Original error:', error);

  if (error instanceof AppError) {
    return error;
  }

  const errorMessage = getErrorMessage(error);
  
  // Check for known error patterns
  for (const [pattern, mapping] of Object.entries(ERROR_MAPPINGS)) {
    if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
      return new AppError(mapping.type, mapping.message, error, errorMessage);
    }
  }

  // Handle PostgreSQL error codes
  if (typeof error === 'object' && error !== null) {
    const pgError = error as any;
    if (pgError.code) {
      switch (pgError.code) {
        case '23505': // unique_violation
          return new AppError(
            ErrorType.VALIDATION,
            'This item already exists. Please use a different value.',
            error,
            errorMessage
          );
        case '23503': // foreign_key_violation
          return new AppError(
            ErrorType.VALIDATION,
            'Unable to complete action due to data relationships.',
            error,
            errorMessage
          );
        case '23514': // check_violation
          return new AppError(
            ErrorType.VALIDATION,
            'The provided data does not meet the required criteria.',
            error,
            errorMessage
          );
        case '42501': // insufficient_privilege
          return new AppError(
            ErrorType.AUTHORIZATION,
            'You do not have permission to perform this action.',
            error,
            errorMessage
          );
      }
    }
  }

  // Default to server error for unknown errors
  return new AppError(
    ErrorType.SERVER,
    'An unexpected error occurred. Please try again or contact support if the problem persists.',
    error,
    errorMessage
  );
}

// Extract error message from various error types
function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'object' && error !== null) {
    const errorObj = error as any;
    return errorObj.message || errorObj.error || errorObj.details || String(error);
  }
  
  return 'Unknown error occurred';
}

// Service wrapper for handling errors
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  customErrorMessage?: string
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    const appError = parseError(error);
    
    // Use custom message if provided
    if (customErrorMessage) {
      throw new AppError(appError.type, customErrorMessage, error);
    }
    
    throw appError;
  }
}

// Toast error handler
export function handleError(error: unknown, showToast: boolean = true): AppError {
  const appError = parseError(error);
  
  if (showToast) {
    toast({
      title: "Error",
      description: appError.userMessage,
      variant: "destructive",
    });
  }
  
  return appError;
}

// Form error handler
export function getFormError(error: unknown): string {
  const appError = parseError(error);
  return appError.userMessage;
}
